import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Crown, Check, Sparkles, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PLANS = [
  {
    id: 'free',
    name: 'Gratuit',
    price: '0',
    tagline: 'Pour découvrir OpenSapiens',
    features: [
      'Profil public',
      'Feed social illimité',
      'Messages directs',
      'Jusqu\'à 3 types de sessions',
      'Commission plateforme 10% sur les ventes',
    ],
    cta: 'Plan actuel',
  },
  {
    id: 'monthly',
    name: 'Premium Mensuel',
    price: '19',
    tagline: 'Pour les créateurs réguliers',
    features: [
      'Tout du plan Gratuit',
      'Types de sessions illimités',
      'Commission réduite à 5%',
      'Badge Premium sur le profil',
      'Priorité dans la recherche',
      'Analytics avancés',
    ],
    cta: 'Souscrire',
    highlight: true,
  },
  {
    id: 'yearly',
    name: 'Premium Annuel',
    price: '190',
    suffix: '/an',
    tagline: '2 mois offerts · économie de 38 CHF',
    features: [
      'Tout du plan Premium Mensuel',
      '2 mois offerts',
      'Support prioritaire',
      'Accès anticipé aux nouveautés',
    ],
    cta: 'Souscrire',
  },
];

const STATUS_LABEL = {
  free: 'Gratuit',
  trial: 'Essai',
  active: 'Actif',
  past_due: 'Paiement en attente',
  canceled: 'Annulé',
};

const Subscription = () => {
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/me/subscription`, { withCredentials: true });
        setSub(data);
      } catch {
        setSub({ status: 'free', plan: null });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleSubscribe = (planId) => {
    // PR4/P1 — plug Stripe subscription checkout here
    toast.info('Le paiement par abonnement arrive bientôt', {
      description: 'Nous travaillons à l\'intégration Stripe pour activer cette fonctionnalité.',
    });
  };

  const handleManage = () => {
    toast.info('Gestion d\'abonnement bientôt disponible');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-gray-500">
        <Loader2 className="h-6 w-6 animate-spin mr-2" /> Chargement...
      </div>
    );
  }

  const currentPlan = sub?.plan || 'free';
  const statusLabel = STATUS_LABEL[sub?.status || 'free'];

  return (
    <div className="space-y-6" data-testid="subscription-page">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Mon abonnement</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Choisissez le plan qui vous correspond et débloquez tout le potentiel d'OpenSapiens.
        </p>
      </div>

      {/* Current status card */}
      <div
        data-testid="subscription-status-card"
        className="p-5 rounded-2xl bg-gradient-to-br from-purple-600 via-fuchsia-600 to-pink-600 text-white shadow-xl"
      >
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-wider opacity-90">
              <Crown className="h-4 w-4" /> Statut actuel
            </div>
            <p className="text-2xl font-bold mt-1">
              {currentPlan === 'free' ? 'Plan Gratuit' : currentPlan === 'monthly' ? 'Premium Mensuel' : 'Premium Annuel'}
            </p>
            <p className="text-sm opacity-90 mt-1">État : {statusLabel}{sub?.ends_at ? ` · renouvellement le ${new Date(sub.ends_at).toLocaleDateString('fr-CH')}` : ''}</p>
          </div>
          {currentPlan !== 'free' && (
            <button
              data-testid="manage-subscription-btn"
              onClick={handleManage}
              className="px-4 py-2 bg-white/15 hover:bg-white/25 backdrop-blur rounded-xl text-sm font-medium"
            >Gérer mon abonnement</button>
          )}
        </div>
      </div>

      {/* Plans grid */}
      <div className="grid md:grid-cols-3 gap-4" data-testid="subscription-plans">
        {PLANS.map((p) => {
          const isCurrent = currentPlan === p.id;
          return (
            <div
              key={p.id}
              data-testid={`plan-card-${p.id}`}
              className={`relative p-5 rounded-2xl border bg-white dark:bg-gray-800 transition-all ${
                p.highlight
                  ? 'border-purple-400 dark:border-purple-600 shadow-lg shadow-purple-100 dark:shadow-purple-900/20'
                  : 'border-gray-200 dark:border-gray-700'
              }`}
            >
              {p.highlight && (
                <span className="absolute -top-3 left-5 px-2 py-0.5 text-[11px] font-semibold bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-full inline-flex items-center gap-1">
                  <Sparkles className="h-3 w-3" /> Populaire
                </span>
              )}
              <h3 className="font-semibold text-lg text-gray-900 dark:text-white">{p.name}</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{p.tagline}</p>
              <div className="mt-3 flex items-baseline gap-1">
                <span className="text-3xl font-bold text-gray-900 dark:text-white">{p.price}</span>
                <span className="text-sm text-gray-500 dark:text-gray-400">CHF{p.suffix || (p.id === 'monthly' ? '/mois' : '')}</span>
              </div>
              <ul className="mt-4 space-y-2">
                {p.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                    <Check className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" /> {f}
                  </li>
                ))}
              </ul>
              <button
                data-testid={`plan-cta-${p.id}`}
                onClick={() => (isCurrent ? null : handleSubscribe(p.id))}
                disabled={isCurrent}
                className={`w-full mt-5 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                  isCurrent
                    ? 'bg-gray-100 dark:bg-gray-700 text-gray-500 cursor-default'
                    : p.highlight
                      ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:shadow-lg'
                      : 'bg-gray-900 text-white hover:bg-gray-800 dark:bg-white dark:text-gray-900'
                }`}
              >
                {isCurrent ? 'Plan actuel' : p.cta}
              </button>
            </div>
          );
        })}
      </div>

      <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
        Paiement sécurisé via Stripe · annulation possible à tout moment · TVA incluse
      </p>
    </div>
  );
};

export default Subscription;
